/**
 * §13.1.6 Level-Up Selection. Full-screen modal, sim paused.
 *
 * The card body is R5's `delta_text` verbatim — the UI never composes a stat string
 * and never renders a raw stat key. A card with an empty `delta_text` is a data bug,
 * so it renders a visible fallback rather than an empty card (§13.1.6 acceptance:
 * 100% of cards show a non-empty delta).
 *
 * Counter-synergy (§6.5) greys the synergy line and states it plainly. It is never
 * hidden.
 *
 * Mobile-first: three cards stack vertically at 390 px and sit side by side from
 * 720 px up. Each card is its own ≥ 44 px touch target across its full width.
 */

import { button, div, setClass, setDelta, setHidden, setText } from '../dom';
import { formatInt } from '../format';
import { iconFor } from '../icons';
import type { LevelUpState, OfferCard } from '../types';
import { Screen, type ActionSink } from './base';

const CARD_COUNT = 3;

/**
 * Drops the item's own name off the front of its description, because the card prints that
 * name in 18 px bold two lines above.
 *
 * `newWeaponDelta` and `newPassiveDelta` in `src/sim/progression.ts` both build
 * `name + ' — ' + blurb`, which is right for §9.12's Graft — that offer has no separate title
 * to carry it — and redundant here. Measured on a level-2 offer at 390×844: the two
 * first-acquisition cards read "Jet Siphon — +22% projectile speed" and "Mitotic Bloom — a
 * bolt that…" under headings saying exactly that, and stood 214 px against the upgrade card's
 * 154 px, a 60 px spread on a screen whose entire job is comparing three options side by side.
 *
 * Stripped HERE rather than in the sim, for two reasons: the Graft still needs the prefix, and
 * `delta_text` crossing the sim boundary is frozen by `INTERFACES.md`. Presentation knows
 * something the sim does not — that the name is already on screen — so presentation is where
 * the knowledge belongs.
 *
 * Exact match only, on the em dash the builders actually emit. A description that merely
 * starts with the same words keeps them.
 */
function withoutLeadingName(text: string, name: string): string {
  if (name.length === 0) return text;
  const prefix = name + ' — ';
  if (!text.startsWith(prefix)) return text;
  const rest = text.slice(prefix.length);
  if (rest.length === 0) return text;
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}
const KIND_LABEL: Record<OfferCard['kind'], string> = {
  weapon: 'Weapon',
  passive: 'Passive',
  evolution: 'Evolution',
  active: 'Active',
};

interface CardRefs {
  root: HTMLButtonElement;
  glyph: SVGUseElement;
  name: HTMLElement;
  tag: HTMLElement;
  level: HTMLElement;
  delta: HTMLElement;
  synergy: HTMLElement;
  index: number;
}

export class LevelUpScreen extends Screen {
  private header: HTMLElement;
  private rerollBtn: HTMLButtonElement;
  private cards: CardRefs[] = [];
  private offers: readonly OfferCard[] = [];
  private openedAtMs = 0;

  constructor(emit: ActionSink) {
    super('levelup', emit, 'is-modal');

    const head = div('levelup-head');
    this.header = div('screen-title', 'Level 1 — choose one');
    head.appendChild(this.header);
    this.rerollBtn = button('Reroll (3)', 'ghost', 'refresh');
    this.rerollBtn.addEventListener('click', () => this.emit({ type: 'reroll' }));
    head.appendChild(this.rerollBtn);
    this.root.appendChild(head);

    const grid = div('card-grid');
    for (let i = 0; i < CARD_COUNT; i++) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'card';
      card.setAttribute('aria-label', 'Upgrade option ' + (i + 1));

      const top = div('card-top');
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'ic card-icon');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttribute('href', '#i-bolt');
      svg.appendChild(use);
      top.appendChild(svg);
      const idx = div('card-index', String(i + 1));
      top.appendChild(idx);
      card.appendChild(top);

      const name = div('card-name', '');
      const tag = div('card-tag', '');
      const level = div('card-level', '');
      const delta = div('card-delta', '');
      const synergy = div('card-synergy', '');
      card.appendChild(name);
      const meta = div('card-meta');
      meta.appendChild(tag);
      meta.appendChild(level);
      card.appendChild(meta);
      card.appendChild(delta);
      card.appendChild(synergy);

      const refs: CardRefs = { root: card, glyph: use, name, tag, level, delta, synergy, index: i };
      card.addEventListener('click', () => this.pick(i));
      grid.appendChild(card);
      this.cards.push(refs);
    }
    this.root.appendChild(grid);

    const hint = div('levelup-hint', 'Pick one to continue.');
    this.root.appendChild(hint);

    this.stops = [this.cards[0].root, this.cards[1].root, this.cards[2].root, this.rerollBtn];
  }

  /** §13.1.6 focus order: card 1 → 2 → 3 → reroll. */
  present(state: LevelUpState, nowMs: number): void {
    this.offers = state.offers;
    this.openedAtMs = nowMs;
    setText(this.header, 'Level ' + formatInt(state.level) + ' — choose one');
    setText(this.rerollBtn.querySelector('.btn-label')!, 'Reroll (' + state.rerolls_left + ')');
    this.rerollBtn.disabled = state.rerolls_left <= 0;
    setClass(this.rerollBtn, 'is-disabled', state.rerolls_left <= 0);

    for (let i = 0; i < CARD_COUNT; i++) {
      const c = this.cards[i];
      const o = i < state.offers.length ? state.offers[i] : null;
      if (!o) {
        setHidden(c.root, true);
        continue;
      }
      setHidden(c.root, false);
      // Fall through to the content id before the kind fallback, or every weapon
      // card draws the same bolt and every passive card the same star.
      c.glyph.setAttribute('href', '#i-' + iconFor(o.icon ?? o.id, o.kind));
      setText(c.name, o.display_name);
      setText(c.tag, KIND_LABEL[o.kind]);
      // AMD-13: the level step is a change, so it reads as a bracketed delta in the
      // surge family — `(Lv 4 → 5)` — and a brand-new pick is green like fresh XP.
      if (o.level_from > 0) setDelta(c.level, 'Lv ' + o.level_from + ' → ' + o.level_to, 'surge');
      else setDelta(c.level, 'new', 'xp');
      setText(
        c.delta,
        o.delta_text && o.delta_text.length > 0
          ? withoutLeadingName(o.delta_text, o.display_name)
          : 'No description available for this offer.',
      );
      setClass(c.root, 'is-evolution', o.kind === 'evolution');
      setClass(c.root, 'is-empty-delta', !o.delta_text || o.delta_text.length === 0);

      if (o.counter_synergy) {
        setHidden(c.synergy, false);
        setText(c.synergy, o.synergy_text && o.synergy_text.length > 0 ? o.synergy_text : 'No effect on your current weapons');
        setClass(c.synergy, 'is-counter', true);
      } else if (o.synergy_text && o.synergy_text.length > 0) {
        setHidden(c.synergy, false);
        setText(c.synergy, o.synergy_text);
        setClass(c.synergy, 'is-counter', false);
      } else {
        setHidden(c.synergy, true);
      }
      c.root.setAttribute('aria-label', o.display_name + '. ' + o.delta_text);
    }

    const stops: HTMLElement[] = [];
    for (let i = 0; i < CARD_COUNT; i++) {
      if (!this.cards[i].root.hidden) stops.push(this.cards[i].root);
    }
    stops.push(this.rerollBtn);
    this.stops = stops;
  }

  /** §15 telemetry proxy for the §13.1.6 "pick-and-understand ≤ 10 s median" bar. */
  openMs(nowMs: number): number {
    return this.openedAtMs > 0 ? nowMs - this.openedAtMs : 0;
  }

  private pick(index: number): void {
    const o = index < this.offers.length ? this.offers[index] : null;
    if (!o) return;
    this.emit({ type: 'pick_offer', index, offer_id: o.id });
  }

  /** 1 / 2 / 3 select, R reroll (§3.1). */
  handleKey(e: KeyboardEvent): boolean {
    if (e.code === 'Digit1') {
      this.pick(0);
      return true;
    }
    if (e.code === 'Digit2') {
      this.pick(1);
      return true;
    }
    if (e.code === 'Digit3') {
      this.pick(2);
      return true;
    }
    if (e.code === 'KeyR') {
      if (!this.rerollBtn.disabled) this.emit({ type: 'reroll' });
      return true;
    }
    return false;
  }

  /** A level-up modal cannot be dismissed — a choice is mandatory (§13.2). */
  override onBack(): boolean {
    return true;
  }
}
